import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { Avatar, AvatarFallback } from '@repo/ui/avatar';
import { DataTable } from '@repo/ui/data-table';
import { formatDate } from '@repo/ui/lib/formatters';
import { Skeleton } from '@repo/ui/skeleton';
import { TableCell } from '@repo/ui/table';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import type { RouterOutputs } from '@/providers/trpc-client';
import { OrganizationsTableActions } from './organizations-table-actions';
import { OrganizationsTableEmptyState } from './organizations-table-empty-state';
import { OrganizationsTableErrorState } from './organizations-table-error-state';
import {
  OrganizationsTableFilters,
  type OrganizationsTableFiltersProps,
} from './organizations-table-filters';

interface OrganizationsTableProps extends Omit<OrganizationsTableFiltersProps, 'hasFilters'> {
  data: RouterOutputs['admin']['listOrganizations']['data'];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRefetch: () => void;
}

export function OrganizationsTable({
  data,
  isLoading,
  isError,
  error,
  onRefetch,
  filters,
  onFilterChange,
  onClearFilters,
}: OrganizationsTableProps) {
  useLocale();

  const columns = useMemo<ColumnDef<RouterOutputs['admin']['listOrganizations']['data'][number]>[]>(
    () => [
      {
        accessorKey: 'name',
        get header() {
          return t('workspace.organizationName');
        },
        cell: ({ row }) => {
          const org = row.original;
          return (
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs">
                  {org.name ? org.name.substring(0, 2).toUpperCase() : '?'}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-sm">{org.name || 'N/A'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'country',
        get header() {
          return t('auth.country');
        },
        cell: ({ row }) => (
          <div className="text-muted-foreground text-sm">{row.original.country || 'N/A'}</div>
        ),
      },
      {
        accessorKey: 'owner',
        get header() {
          return t('workspace.owner');
        },
        cell: ({ row }) => (
          <div className="text-muted-foreground text-sm">{row.original?.owner?.email || 'N/A'}</div>
        ),
      },
      {
        accessorKey: 'createdAt',
        get header() {
          return t('workspace.createdAt');
        },
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-muted-foreground text-sm">
            {row.original.createdAt ? formatDate(row.original.createdAt) : 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        get header() {
          return t('workspace.accountStatus');
        },
        cell: ({ row }) => <OrganizationsTableActions organization={row.original} />,
      },
    ],
    [getLocale()],
  );

  return (
    <div className="space-y-4">
      <OrganizationsTableFilters
        filters={filters}
        hasFilters={!!filters.search}
        onClearFilters={onClearFilters}
        onFilterChange={onFilterChange}
      />
      <DataTable
        columns={columns}
        data={data}
        emptyState={
          <OrganizationsTableEmptyState
            hasFilters={!!filters.search}
            onClearFilters={onClearFilters}
            searchQuery={filters.search}
          />
        }
        error={{
          enable: isError || false,
          component: (
            <TableCell className="h-32 text-center" colSpan={columns.length}>
              <OrganizationsTableErrorState error={error} onRefetch={onRefetch} />
            </TableCell>
          ),
        }}
        skeleton={{
          enable: isLoading,
          rows: 5,
          component: columns.map((_, colIndex) => (
            <TableCell key={`skeleton-col-${colIndex}`}>
              <Skeleton className="h-8 w-full" />
            </TableCell>
          )),
        }}
      />
    </div>
  );
}
